import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CloseIcon, HStack, Popover, useTheme, VStack } from 'native-base';
import { map } from 'lodash';

import {
  ChevronSelectorVerticalIcon,
  Download02Icon,
  Lock01Icon,
  MinusCircleIcon,
  MoveIcon,
  ReverseLeftIcon,
  SwitchHorizontalIcon,
  Text,
} from '@pimm/base';
import { PinIcon } from 'lucide-react';

type PopoverTooltipPositionActionsProps = Omit<ComponentProps<typeof Popover>, 'children'> & {};

export const PopoverTooltipPositionActions = (props: PopoverTooltipPositionActionsProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [tips] = useState<
    {
      title: string;
      tips: { boxProps?: ComponentProps<typeof Box>; icon?: React.ReactNode; title: string; description: string }[];
    }[]
  >([
    {
      title: 'positioning_plan:position_actions',
      tips: [
        {
          icon: <ChevronSelectorVerticalIcon size="20px" color="gray.700" />,
          title: 'positioning_plan:change_primary_position',
          description: 'positioning_plan:override_primary_position',
        },
        {
          icon: <ReverseLeftIcon size="14px" color="gray.700" />,
          title: 'positioning_plan:restore_primary_position',
          description: 'positioning_plan:restore_primary_position_default',
        },
        {
          icon: <MinusCircleIcon size="18px" color="gray.700" />,
          title: 'positioning_plan:non_service_position',
          description: 'positioning_plan:designate_non_service_employee',
        },
        {
          icon: <MoveIcon size="18px" color="gray.700" />,
          title: 'positioning_plan:move_employee',
          description: 'positioning_plan:edit_mode_assign_swap',
        },
        {
          icon: <PinIcon size={18} color={colors.gray[600]} />,
          boxProps: { borderWidth: 0, bgColor: 'gray.25', shadow: undefined },
          title: 'positioning_plan:pinned_core_position',
          description: 'positioning_plan:pinned_core_position_description',
        },
        {
          icon: <Lock01Icon size="18px" color="gray.600" />,
          boxProps: { opacity: 0.6 },
          title: 'positioning_plan:locked_core_position',
          description: 'positioning_plan:locked_core_position_description',
        },
      ],
    },
    {
      title: 'positioning_plan:edit_mode',
      tips: [
        {
          boxProps: { borderColor: 'primary.500', bgColor: 'primary.500' },
          icon: <SwitchHorizontalIcon size="18px" color="black" />,
          title: 'positioning_plan:swap_employee',
          description: 'positioning_plan:swap_employees_positions',
        },
        {
          boxProps: { borderColor: 'primary.500', bgColor: 'primary.500' },
          icon: <Download02Icon size="18px" color="black" />,
          title: 'positioning_plan:assign_employee',
          description: 'positioning_plan:assign_employee_to_position',
        },
        {
          boxProps: { borderColor: 'black', bgColor: 'black' },
          icon: <CloseIcon size="14px" color="white" />,
          title: 'positioning_plan:exit_edit_mode',
          description: 'positioning_plan:cancel_position_editing',
        },
      ],
    },
  ]);
  return (
    <Popover isKeyboardDismissable _backdrop={{ bg: 'black' }} {...props}>
      <Popover.Content accessibilityLabel="Position Actions Tooltip" rounded="lg" bg="white" borderWidth={0}>
        <Popover.Arrow zIndex={1} borderWidth={0} borderColor="white" bg="white" />
        <Popover.Body py={3} px={4} w="320" borderWidth={0} bg="white">
          <VStack space={2}>
            {tips.map(_tip => {
              return (
                <Box key={_tip.title}>
                  <Text size="lg" fontWeight={600} color="black" lineHeight="xs" mb={2}>
                    {t(_tip.title)}
                  </Text>

                  <VStack space={1}>
                    {map(_tip.tips, _ => (
                      <HStack key={_.title} space={3} py={1} minH={10}>
                        <Box
                          rounded="md"
                          alignItems="center"
                          justifyContent="center"
                          boxSize={8}
                          borderWidth={1}
                          borderColor="gray.300"
                          bg="white"
                          shadow={1}
                          {..._.boxProps}
                        >
                          {_.icon}
                        </Box>
                        <VStack space={1} flex={1} pr={2}>
                          <Text size="md" fontWeight={600} color="black" lineHeight="xs">
                            {t(_.title)}
                          </Text>
                          <Text size="md" fontWeight={500} color="gray.700" numberOfLines={3} lineHeight="sm" lineBreakMode="tail">
                            {t(_.description)}
                          </Text>
                        </VStack>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
